// Sidebar: object palette, shape-drawing controls, and the two scrollable lists
// (objects / shapes) with a uniform inline edit panel (Name · Einstellungen · Bilder).
import { getState, markerDisplayNums, scheduleSave } from "../state";
import {
  KIND_LABEL_PLURAL,
  flaecheCats,
  markerCatById,
  markerCats,
  shapeCatById,
} from "../catalog";
import {
  session,
  setMode,
  setShapeCat,
  setLineClosed,
  setMultiPlace,
  select,
  deselect,
} from "../session";
import { renderOverlay } from "../overlay";
import { centerOnMarker, centerOnShape, removeMarker, removeShape } from "../interaction";
import { iconMarkup } from "../icons";
import { createImageListField } from "./imagefield";
import type { Marker, Shape } from "../types";

interface SidebarEls {
  palette: HTMLElement;
  objList: HTMLElement;
  shapeList: HTMLElement;
  shapeCatsWrap: HTMLElement;
  closedRow: HTMLElement;
  lineClosed: HTMLInputElement;
  multiPlace: HTMLInputElement;
  btnDrawShape: HTMLButtonElement;
  placeHint: HTMLElement;
  shapeHint: HTMLElement;
}

let els: SidebarEls;

const esc = (s: string): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function initSidebar(elements: SidebarEls): void {
  els = elements;
  buildPalette();
  els.multiPlace.addEventListener("change", () => setMultiPlace(els.multiPlace.checked));
  els.lineClosed.addEventListener("change", () => setLineClosed(els.lineClosed.checked));
  els.btnDrawShape.addEventListener("click", () => setMode("shape"));
  buildShapeCats();
  syncShapeControls();
}

/* ------------------------------- palette ------------------------------- */

function buildPalette(): void {
  els.palette.replaceChildren();
  for (const cat of markerCats()) {
    const b = document.createElement("button");
    b.className = "palette-btn";
    b.dataset.cat = cat.id;
    b.style.setProperty("--cat-color", cat.color);
    b.innerHTML = `<span class="swatch" style="background:${cat.color}">${iconMarkup(cat.icon)}</span><span>${esc(cat.label)}</span>`;
    b.addEventListener("click", () => {
      const s = session();
      if (s.mode === "place" && s.placeCat === cat.id) setMode("select");
      else setMode("place", cat.id);
    });
    els.palette.appendChild(b);
  }
}

function buildShapeCats(): void {
  const s = session();
  const list = flaecheCats();
  els.shapeCatsWrap.replaceChildren();

  if (list.length === 0) {
    const note = document.createElement("div");
    note.className = "empty-note";
    note.style.padding = "6px 2px";
    note.textContent = 'Noch keine Flächen-Kategorien – im Tab „Einstellungen" anlegen.';
    els.shapeCatsWrap.appendChild(note);
    return;
  }

  for (const cat of list) {
    const b = document.createElement("button");
    b.className = "zone-cat-btn" + (cat.id === s.shapeCat ? " active" : "");
    b.dataset.cat = cat.id;
    b.style.setProperty("--cat-color", cat.color);
    b.innerHTML = `<span class="swatch" style="background:${cat.color}"></span><span>${esc(cat.label)}</span>`;
    b.addEventListener("click", () => setShapeCat(cat.id));
    els.shapeCatsWrap.appendChild(b);
  }
}

function syncShapeControls(): void {
  const s = session();
  els.closedRow.style.display = "none";
  els.multiPlace.checked = s.multiPlace;
  els.btnDrawShape.textContent = "+ Fläche zeichnen";
  els.shapeHint.innerHTML =
    `Eckpunkte der Fläche klicken. Doppelklick oder <span class="mono">Enter</span> ` +
    `schließt ab (mind. 3 Punkte). <span class="mono">Esc</span> bricht ab.`;
  els.placeHint.classList.toggle("show", s.mode === "place");
  els.shapeHint.classList.toggle("show", s.mode === "shape");
  els.palette.querySelectorAll(".palette-btn").forEach((b) => {
    b.classList.toggle("active", s.mode === "place" && (b as HTMLElement).dataset.cat === s.placeCat);
  });
  els.shapeCatsWrap.querySelectorAll(".zone-cat-btn").forEach((b) => {
    b.classList.toggle("active", (b as HTMLElement).dataset.cat === s.shapeCat);
  });
}

/* --------------------------- shared edit panel --------------------------- */

interface EditFields {
  /** displayed in the row title + placeholder */
  fallbackName: string;
  name: string;
  note: string;
  images: string[];
  imageUrls?: string[];
  locked: boolean;
  /** extra settings markup inserted after the Name field */
  extra?: string;
}

function buildEditPanel(
  fields: EditFields,
  hooks: {
    onName: (v: string) => void;
    onNote: (v: string) => void;
    onImages: (files: string[], urls: string[]) => void;
    onDelete: () => void;
    onToggleLock: () => void;
    wireExtra?: (panel: HTMLElement) => void;
  }
): HTMLElement {
  const readOnly = session().readOnly;
  const panel = document.createElement("div");
  panel.className = "edit-panel";
  panel.innerHTML = `
    <div class="field"><label>Name</label><input type="text" data-f="name" placeholder="${esc(fields.fallbackName)}" value="${esc(fields.name)}"${readOnly ? " disabled" : ""}></div>
    ${fields.extra ?? ""}
    <div class="field"><label>Notiz</label><textarea data-f="note" placeholder="Notiz…"${readOnly ? " disabled" : ""}>${esc(fields.note)}</textarea></div>
    <div class="field" data-slot="image"><label>Bilder</label></div>
    ${
      readOnly
        ? ""
        : `<div class="edit-actions">
      <button type="button" class="btn sm ghost" data-a="lock">${fields.locked ? "Entsperren" : "Sperren"}</button>
      <button class="btn sm ghost" data-a="delete" style="color:var(--danger); margin-left:auto;">Löschen</button>
    </div>`
    }`;

  const nameInput = panel.querySelector<HTMLInputElement>('[data-f="name"]')!;
  nameInput.addEventListener("input", () => hooks.onName(nameInput.value));
  const noteInput = panel.querySelector<HTMLTextAreaElement>('[data-f="note"]')!;
  noteInput.addEventListener("input", () => hooks.onNote(noteInput.value));

  panel.querySelector('[data-slot="image"]')!.appendChild(
    createImageListField({
      urls: fields.imageUrls ?? fields.images.map((f) => `/uploads/${f}`),
      onChange: hooks.onImages,
      readOnly,
    })
  );

  if (readOnly) return panel;

  hooks.wireExtra?.(panel);
  const lockBtn = panel.querySelector<HTMLButtonElement>('[data-a="lock"]')!;
  lockBtn.addEventListener("click", () => {
    hooks.onToggleLock();
    lockBtn.textContent = lockBtn.textContent === "Sperren" ? "Entsperren" : "Sperren";
  });
  panel.querySelector('[data-a="delete"]')!.addEventListener("click", hooks.onDelete);
  return panel;
}

function setRowTitle(row: HTMLElement, text: string, imageCount: number): void {
  const title = row.querySelector(".item-title");
  if (!title) return;
  title.textContent = text;
  if (imageCount) {
    const tag = document.createElement("span");
    tag.className = "img-tag";
    tag.textContent = imageCount > 1 ? ` ◐${imageCount}` : " ◐";
    title.appendChild(tag);
  }
}

/* ----------------------------- object list ----------------------------- */

function buildMarkerRow(m: Marker, displayNum: number): HTMLElement {
  const cat = markerCatById(m.category);
  const s = session();
  const open = s.selection?.type === "marker" && s.selection.id === m.id;

  const row = document.createElement("div");
  row.className = `item-row${open ? " open" : ""}`;
  row.dataset.row = `marker-${m.id}`;

  const head = document.createElement("div");
  head.className = `item-row-head${open ? " selected" : ""}`;
  head.innerHTML = `
    <span class="num-badge" style="background:${cat.color}">${displayNum}</span>
    <span class="grow">
      <div class="item-title">${esc(m.label || cat.label)}${m.images.length ? ' <span class="img-tag">◐</span>' : ""}</div>
      ${m.note ? `<div class="item-sub">${esc(m.note)}</div>` : ""}
    </span>
    <svg class="chev" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 4l6 6-6 6"/></svg>`;
  head.addEventListener("click", () => {
    if (open) deselect();
    else {
      select({ type: "marker", id: m.id });
      centerOnMarker(m);
    }
  });
  row.appendChild(head);

  row.appendChild(
    buildEditPanel(
      { fallbackName: cat.label, name: m.label, note: m.note, images: m.images, imageUrls: m.imageUrls, locked: m.locked },
      {
        onName: (v) => {
          m.label = v;
          scheduleSave();
          setRowTitle(row, m.label || cat.label, m.images.length);
          renderOverlay();
        },
        onNote: (v) => {
          m.note = v;
          scheduleSave();
        },
        onImages: (files, urls) => {
          m.images = files;
          m.imageUrls = urls;
          scheduleSave();
          setRowTitle(row, m.label || cat.label, m.images.length);
        },
        onToggleLock: () => {
          m.locked = !m.locked;
          scheduleSave();
          renderOverlay();
        },
        onDelete: () => removeMarker(m.id),
      }
    )
  );
  return row;
}

function renderObjList(): void {
  const { markers } = getState();
  els.objList.replaceChildren();
  if (markers.length === 0) {
    els.objList.innerHTML = '<div class="empty-note">Noch keine Objekte platziert.</div>';
    return;
  }
  const nums = markerDisplayNums();
  for (const cat of markerCats()) {
    const items = markers.filter((m) => m.category === cat.id).sort((a, b) => a.num - b.num);
    if (!items.length) continue;
    const head = document.createElement("div");
    head.className = "list-group-title";
    head.innerHTML = `<span>${esc(cat.label)}</span><span class="count-pill">${items.length}</span>`;
    els.objList.appendChild(head);
    for (const m of items) els.objList.appendChild(buildMarkerRow(m, nums.get(m.id) ?? m.num));
  }
}

/* ------------------------------ shape list ------------------------------ */

function buildShapeRow(z: Shape): HTMLElement {
  const cat = shapeCatById(z.kind, z.category);
  const s = session();
  const open = s.selection?.type === "shape" && s.selection.id === z.id;

  const row = document.createElement("div");
  row.className = `item-row${open ? " open" : ""}`;
  row.dataset.row = `shape-${z.id}`;

  const head = document.createElement("div");
  head.className = `item-row-head${open ? " selected" : ""}`;
  head.innerHTML = `
    <span class="swatch" style="background:${cat.color}; border-radius:6px;"></span>
    <span class="grow">
      <div class="item-title">${esc(z.name || cat.label)}${z.images.length ? ' <span class="img-tag">◐</span>' : ""}</div>
      <div class="item-sub">${esc(cat.label)}</div>
    </span>
    <svg class="chev" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 4l6 6-6 6"/></svg>`;
  head.addEventListener("click", () => {
    if (open) deselect();
    else {
      select({ type: "shape", id: z.id });
      centerOnShape(z);
    }
  });
  row.appendChild(head);

  const extra =
    z.kind === "line"
      ? `<label class="check-row"><input type="checkbox" data-f="closed"${z.closed ? " checked" : ""}> Als Schlaufe geschlossen</label>`
      : "";

  row.appendChild(
    buildEditPanel(
      { fallbackName: cat.label, name: z.name, note: z.note, images: z.images, imageUrls: z.imageUrls, locked: z.locked, extra },
      {
        onName: (v) => {
          z.name = v;
          scheduleSave();
          setRowTitle(row, z.name || cat.label, z.images.length);
          renderOverlay();
        },
        onNote: (v) => {
          z.note = v;
          scheduleSave();
        },
        onImages: (files, urls) => {
          z.images = files;
          z.imageUrls = urls;
          scheduleSave();
          setRowTitle(row, z.name || cat.label, z.images.length);
        },
        onToggleLock: () => {
          z.locked = !z.locked;
          scheduleSave();
          renderOverlay();
        },
        onDelete: () => removeShape(z.id),
        wireExtra: (panel) => {
          const cb = panel.querySelector<HTMLInputElement>('[data-f="closed"]');
          cb?.addEventListener("change", () => {
            z.closed = cb.checked;
            scheduleSave();
            renderOverlay();
          });
        },
      }
    )
  );
  return row;
}

function renderShapeList(): void {
  const { shapes } = getState();
  els.shapeList.replaceChildren();
  if (shapes.length === 0) {
    els.shapeList.innerHTML =
      '<div class="empty-note">Noch keine Flächen oder Tensator-Absperrungen.</div>';
    return;
  }
  const groups: Array<{ title: string; match: (z: Shape) => boolean }> = [
    { title: "Flächen", match: (z) => z.kind === "area" || z.kind === "object" },
    { title: KIND_LABEL_PLURAL.line, match: (z) => z.kind === "line" },
  ];
  for (const g of groups) {
    const items = shapes.filter(g.match);
    if (!items.length) continue;
    const head = document.createElement("div");
    head.className = "list-group-title";
    head.innerHTML = `<span>${g.title}</span><span class="count-pill">${items.length}</span>`;
    els.shapeList.appendChild(head);
    for (const z of items) els.shapeList.appendChild(buildShapeRow(z));
  }
}

export function renderSidebar(): void {
  buildPalette();
  buildShapeCats();
  syncShapeControls();
  renderObjList();
  renderShapeList();
}
